'use client';

import { c as _c } from "react/compiler-runtime";
import React, { type ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';
import { CircleCheck, CircleX, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
const iconsByType: Record<'success' | 'error' | 'info', ReactNode> = {
  success: <CircleCheck size={20} />,
  error: <CircleX size={20} />,
  info: <Info size={20} />
};
export function toast(props: Omit<ToastProps, 'id'> & {
  duration?: number;
}) {
  return sonnerToast.custom(id => <Toast id={id} type={props.type} title={props?.title} description={props?.description} containerClassName={props?.containerClassName} />, {
    duration: props.duration
  });
}
toast.dismiss = (id: string | number) => {
  sonnerToast.dismiss(id);
};
toast.error = (description: string, options?: {
  duration?: number;
}) => {
  return toast({
    type: 'error',
    description,
    ...options
  });
};
toast.success = (description: string, options?: {
  duration?: number;
}) => {
  return toast({
    type: 'success',
    description,
    ...options
  });
};
toast.info = (description: string, options?: {
  duration?: number;
}) => {
  return toast({
    type: 'info',
    description,
    ...options
  });
};
function Toast(props) {
  const $ = _c(30);
  const {
    id,
    type,
    description,
    title,
    containerClassName
  } = props;
  const t0 = containerClassName ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = cn(t0, "flex w-full toast-mobile:w-[356px] justify-center");
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = title && description && "items-start";
  const t3 = title && !description && "items-center";
  const t4 = !title && !description && "items-center";
  let t5;
  if ($[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = cn("bg-surface p-4 border-muted border rounded-2xl w-full toast-mobile:w-fit flex gap-2 flex-row  shadow-lg", t2, t3, t4);
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = !title && description && "mt-0.5";
  let t7;
  if ($[6] !== t6) {
    t7 = cn("text-primary ", t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = iconsByType[type];
  let t9;
  if ($[8] !== t7 || $[9] !== t8 || $[10] !== type) {
    t9 = <div data-type={type} className={t7}>{t8}</div>;
    $[8] = t7;
    $[9] = t8;
    $[10] = type;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = title && description && "-mt-1";
  let t11;
  if ($[12] !== t10) {
    t11 = cn("flex flex-col", t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== title) {
    t12 = title && <span className="text-base font-medium text-primary">{title}</span>;
    $[14] = title;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== description) {
    t13 = description && <span className="text-sm font-normal text-muted-foreground">{description}</span>;
    $[16] = description;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t11 || $[19] !== t12 || $[20] !== t13) {
    t14 = <div className={t11}>{t12}{t13}</div>;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== id || $[23] !== t14 || $[24] !== t5 || $[25] !== t9) {
    t15 = <div data-testid="toast" key={id} className={t5}>{t9}{t14}</div>;
    $[22] = id;
    $[23] = t14;
    $[24] = t5;
    $[25] = t9;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t1 || $[28] !== t15) {
    t16 = <div className={t1}>{t15}</div>;
    $[27] = t1;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  return t16;
}
interface ToastProps {
  id: string | number;
  type: 'success' | 'error' | 'info';
  title?: string;
  description?: string;
  containerClassName?: string;
}